/**
 * Ref Images Store
 *
 * Per-project, per-user reference image bank.
 * Caches non-hidden bank refs keyed by projectId.
 */

import { create } from 'zustand';
import toast from 'react-hot-toast';
import { listReferenceBank } from '../services/visionAssetsClient.js';

export const useRefImagesStore = create((set, get) => ({
  refsByProject: {},
  loadingProjects: new Set(),

  /**
   * Load ref images for a project (if not already cached).
   */
  loadRefImages: async (projectId) => {
    const state = get();
    if (state.refsByProject[projectId]) return state.refsByProject[projectId];
    if (state.loadingProjects.has(projectId)) {
      // Resolve when the in-flight load finishes — success OR failure. Keying on the cache turning
      // truthy hangs (and leaks this subscription) when the load rejects, since the failure path
      // clears the loading flag without writing the cache.
      return new Promise((resolve) => {
        const unsub = useRefImagesStore.subscribe((s) => {
          if (!s.loadingProjects.has(projectId)) {
            unsub();
            resolve(s.refsByProject[projectId] ?? []);
          }
        });
      });
    }

    set((s) => ({ loadingProjects: new Set([...s.loadingProjects, projectId]) }));
    try {
      const refs = await listReferenceBank(projectId);
      set((s) => ({
        refsByProject: { ...s.refsByProject, [projectId]: refs || [] },
        loadingProjects: new Set([...s.loadingProjects].filter(id => id !== projectId)),
      }));
      return refs || [];
    } catch (error) {
      console.error('[RefImagesStore] Load error:', error);
      toast.error('Could not load reference images');
      set((s) => ({
        loadingProjects: new Set([...s.loadingProjects].filter(id => id !== projectId)),
      }));
      return [];
    }
  },

  /**
   * Add a new ref image to the cache (after upload).
   */
  addRefImage: (projectId, ref) => {
    set((s) => ({
      refsByProject: {
        ...s.refsByProject,
        [projectId]: [ref, ...(s.refsByProject[projectId] || [])],
      },
    }));
  },

  /**
   * Remove a ref image from the cache (after delete).
   */
  removeRefImage: (projectId, refId) => {
    set((s) => ({
      refsByProject: {
        ...s.refsByProject,
        [projectId]: (s.refsByProject[projectId] || []).filter(r => r.id !== refId),
      },
    }));
  },

  /**
   * Move a ref image to the front (recently used).
   */
  bumpRefImage: (projectId, refId) => {
    set((s) => {
      const list = s.refsByProject[projectId] || [];
      const ref = list.find(r => r.id === refId);
      if (!ref) return s;
      return {
        refsByProject: {
          ...s.refsByProject,
          [projectId]: [ref, ...list.filter(r => r.id !== refId)],
        },
      };
    });
  },

  /**
   * Invalidate cache for a project (force re-fetch on next load).
   */
  invalidate: (projectId) => {
    set((s) => {
      const next = { ...s.refsByProject };
      delete next[projectId];
      return { refsByProject: next };
    });
  },

  /**
   * Clear all caches (call on logout).
   */
  reset: () => {
    set({ refsByProject: {}, loadingProjects: new Set() });
  },
}));
